import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Settings, FileText, Save, Mail, Send, CheckCircle, XCircle, Users, CreditCard, Tag, Loader, Image, HardDrive } from 'lucide-react';

// Field component FUORI dal render per evitare re-mount ad ogni keystroke
function SettingField({ label, value, onChange, type = 'text', placeholder = '', help = '' }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input type={type} className="input-field-dark" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      {help && <p className="text-[10px] text-gray-600 mt-1">{help}</p>}
    </div>
  );
}

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testType, setTestType] = useState('info');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Create user form
  const [newUser, setNewUser] = useState({ email: '', password: '', first_name: '', last_name: '', pn_commission_percent: 30 });
  const [creating, setCreating] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/admin/settings'); setSettings(data.settings); }
      catch (e) {} finally { setLoading(false); }
    })();
  }, []);

  // Funzione set stabile
  const set = useCallback((cat, key, val) => {
    setSettings(prev => ({ ...prev, [cat]: { ...(prev[cat] || {}), [key]: val } }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const flat = {};
      Object.entries(settings).forEach(([cat, vals]) => {
        if (vals && typeof vals === 'object') Object.entries(vals).forEach(([k, v]) => { flat[k] = v; });
      });
      await api.put('/admin/settings', { settings: flat });
      toast.success('Impostazioni salvate!');
    } catch (err) { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error('Inserisci un indirizzo email');
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post('/admin/email-test', { to: testEmail, type: testType });
      setTestResult({ success: true, message: data.message, from: data.from });
      toast.success('Email di test inviata!');
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.error || 'Errore', details: err.response?.data?.details });
      toast.error('Errore invio email di test');
    } finally { setTesting(false); }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) return toast.error('Compila tutti i campi');
    setCreating(true);
    try {
      await api.post('/admin/users/create', newUser);
      toast.success(`Utente ${newUser.first_name} ${newUser.last_name} creato e attivato!`);
      setNewUser({ email: '', password: '', first_name: '', last_name: '', pn_commission_percent: 30 });
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setCreating(false); }
  };

  const handleLogoUpload = async (e, type) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('logo', f); fd.append('type', type);
    try {
      const { data } = await api.post('/admin/upload-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('general', type === 'favicon' ? 'site_favicon_path' : 'site_logo_path', data.path);
      toast.success(`${type === 'favicon' ? 'Favicon' : 'Logo'} caricato!`);
    } catch (err) { toast.error('Errore upload'); }
  };

  const handleAppIconUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('logo', f);
    try {
      const { data } = await api.post('/admin/upload-app-icon', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('general', 'app_icon_path', data.path);
      toast.success('Icona app caricata! Generati formati 192x192 e 512x512');
    } catch (err) { toast.error('Errore upload icona'); }
  };

  const runCleanup = async (deleteAudio) => {
    if (!window.confirm(deleteAudio
      ? 'Ridimensionare le copertine a 250x250 e CANCELLARE tutti i file audio delle release approvate/distribuite?'
      : 'Ridimensionare le copertine a 250x250 delle release approvate/distribuite?')) return;
    setCleaning(true);
    setCleanupResult(null);
    try {
      const { data } = await api.post('/admin/cleanup-storage', { cover_size: 250, delete_audio: deleteAudio });
      setCleanupResult(data);
      toast.success(`Pulizia completata! ${data.spaceFreedMB} MB liberati`);
    } catch (err) { toast.error('Errore pulizia'); }
    finally { setCleaning(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400" /></div>;

  const g = settings.general || {};
  const f = settings.finance || {};
  const c = settings.codes || {};
  const em = settings.email || {};

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Settings size={24} className="text-yellow-400" />Impostazioni</h1>
        <button onClick={save} disabled={saving} className="btn-admin inline-flex items-center gap-2"><Save size={16} />{saving ? 'Salvataggio...' : 'Salva Tutto'}</button>
      </div>

      {/* ===== GENERALI ===== */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center gap-2 mb-4"><Settings size={18} className="text-yellow-400" /><h2 className="font-bold text-white">Generali</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingField label="Nome Sito" value={g.site_name} onChange={v => set('general', 'site_name', v)} />
          <div>
            <label className="block text-xs text-gray-400 mb-1">Registrazione Utenti</label>
            <label className="flex items-center gap-3 cursor-pointer mt-2">
              <div className={`w-12 h-6 rounded-full transition relative cursor-pointer ${(g.registration_enabled || 'true') === 'true' ? 'bg-green-500' : 'bg-gray-600'}`}
                onClick={() => set('general', 'registration_enabled', (g.registration_enabled || 'true') === 'true' ? 'false' : 'true')}>
                <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${(g.registration_enabled || 'true') === 'true' ? 'left-6' : 'left-0.5'}`} />
              </div>
              <span className="text-sm text-gray-300">{(g.registration_enabled || 'true') === 'true' ? 'Attiva' : 'Disattivata'}</span>
            </label>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Autenticazione 2 Fattori</label>
            <label className="flex items-center gap-3 cursor-pointer mt-2">
              <div className={`w-12 h-6 rounded-full transition relative cursor-pointer ${(g.two_factor_global || 'true') === 'true' ? 'bg-green-500' : 'bg-red-600'}`}
                onClick={() => set('general', 'two_factor_global', (g.two_factor_global || 'true') === 'true' ? 'false' : 'true')}>
                <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${(g.two_factor_global || 'true') === 'true' ? 'left-6' : 'left-0.5'}`} />
              </div>
              <span className="text-sm text-gray-300">{(g.two_factor_global || 'true') === 'true' ? 'Permessa' : 'Disattivata per tutti'}</span>
            </label>
            <p className="text-[10px] text-gray-600 mt-1">Se disattivata, il 2FA viene ignorato per tutti gli utenti anche se l'hanno attivata</p>
          </div>
          <SettingField label="Messaggio Benvenuto (IT)" value={g.welcome_message_it} onChange={v => set('general', 'welcome_message_it', v)} />
          <SettingField label="Welcome Message (EN)" value={g.welcome_message_en} onChange={v => set('general', 'welcome_message_en', v)} />
        </div>

        {/* Logo Upload */}
        <div className="mt-6 pt-4 border-t border-gray-700">
          <h3 className="text-sm font-medium text-yellow-400 mb-3 flex items-center gap-2"><Image size={16} />Logo, Favicon e Icona App</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-2">Logo (sito + email)</label>
              <div className="flex items-center gap-3">
                {g.site_logo_path && <img src={g.site_logo_path} alt="Logo" className="h-10 bg-white rounded p-1" />}
                <input type="file" accept="image/*" className="hidden" id="logo-upload" onChange={e => handleLogoUpload(e, 'logo')} />
                <label htmlFor="logo-upload" className="btn-admin text-xs cursor-pointer">Carica Logo</label>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Favicon</label>
              <div className="flex items-center gap-3">
                {g.site_favicon_path && <img src={g.site_favicon_path} alt="Favicon" className="h-8" />}
                <input type="file" accept="image/*,.ico" className="hidden" id="favicon-upload" onChange={e => handleLogoUpload(e, 'favicon')} />
                <label htmlFor="favicon-upload" className="btn-admin text-xs cursor-pointer">Carica Favicon</label>
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-400 mb-2">📱 Icona App (PWA)</label>
              <div className="flex items-center gap-3">
                {g.app_icon_path && <img src={g.app_icon_path} alt="App Icon" className="h-12 w-12 rounded-xl object-cover" />}
                <div>
                  <input type="file" accept="image/png,image/jpeg" className="hidden" id="app-icon-upload" onChange={handleAppIconUpload} />
                  <label htmlFor="app-icon-upload" className="btn-admin text-xs cursor-pointer">Carica Icona App</label>
                  <p className="text-[10px] text-gray-500 mt-1">PNG/JPG quadrata, minimo 512×512px. Viene auto-ridimensionata per tutti i dispositivi.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== FINANZA ===== */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center gap-2 mb-1"><CreditCard size={18} className="text-yellow-400" /><h2 className="font-bold text-white">Finanza</h2></div>
        <p className="text-xs text-gray-500 mb-4">Percentuali e parametri finanziari della piattaforma</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingField label="Fee Distributore %" value={f.distributor_fee_percent} onChange={v => set('finance', 'distributor_fee_percent', v)} type="number" help="Percentuale che il distributore trattiene (default: 20%)" />
          <SettingField label="Commissione Default PN %" value={f.default_commission_percent} onChange={v => set('finance', 'default_commission_percent', v)} type="number" help="Commissione default per nuovi utenti (modificabile per utente)" />
          <SettingField label="Payout Minimo (€)" value={f.min_payout_amount} onChange={v => set('finance', 'min_payout_amount', v)} type="number" help="Saldo minimo per richiedere il pagamento" />
          <SettingField label="Valuta" value={f.payout_currency} onChange={v => set('finance', 'payout_currency', v)} />
        </div>
      </div>

      {/* ===== DATI AZIENDA ===== */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center gap-2 mb-1"><Settings size={18} className="text-yellow-400" /><h2 className="font-bold text-white">Dati Azienda</h2></div>
        <p className="text-xs text-gray-500 mb-4">Questi dati appariranno nelle ricevute di pagamento e nei documenti ufficiali</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingField label="Ragione Sociale" value={f.company_name} onChange={v => set('finance', 'company_name', v)} placeholder="ProudNoize S.r.l." />
          <SettingField label="P.IVA" value={f.company_vat} onChange={v => set('finance', 'company_vat', v)} placeholder="IT12345678901" />
          <SettingField label="Codice Fiscale" value={f.company_fiscal_code} onChange={v => set('finance', 'company_fiscal_code', v)} placeholder="12345678901" />
          <SettingField label="Codice SDI" value={f.company_sdi} onChange={v => set('finance', 'company_sdi', v)} placeholder="XXXXXXX" />
          <SettingField label="Indirizzo" value={f.company_address} onChange={v => set('finance', 'company_address', v)} placeholder="Via Roma 1" />
          <SettingField label="CAP" value={f.company_zip} onChange={v => set('finance', 'company_zip', v)} placeholder="00100" />
          <SettingField label="Città" value={f.company_city} onChange={v => set('finance', 'company_city', v)} placeholder="Roma" />
          <SettingField label="Provincia" value={f.company_province} onChange={v => set('finance', 'company_province', v)} placeholder="RM" />
          <SettingField label="PEC" value={f.company_pec} onChange={v => set('finance', 'company_pec', v)} placeholder="pec@proudnoize.com" />
          <SettingField label="IBAN" value={f.company_iban} onChange={v => set('finance', 'company_iban', v)} placeholder="IT60X0542811101000000123456" />
        </div>
      </div>

      {/* ===== FATTURAZIONE ===== */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center gap-2 mb-1"><FileText size={18} className="text-yellow-400" /><h2 className="font-bold text-white">Fatturazione</h2></div>
        <p className="text-xs text-gray-500 mb-4">Impostazioni fiscali per i rendiconti e le fatture. Attiva queste opzioni solo se la tua attività rientra nelle esenzioni previste dalla legge italiana.</p>
        <div className="space-y-4">
          <div className="bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-white font-medium text-sm">Esente IVA</p>
                <p className="text-[10px] text-gray-500">Se attivo, le fatture vengono emesse senza IVA con il riferimento normativo specificato</p>
              </div>
              <button onClick={() => set('finance', 'invoice_vat_exempt', (f.invoice_vat_exempt || 'false') === 'true' ? 'false' : 'true')} className={`w-12 h-6 rounded-full transition-colors ${(f.invoice_vat_exempt || 'false') === 'true' ? 'bg-green-500' : 'bg-gray-600'}`}>
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${(f.invoice_vat_exempt || 'false') === 'true' ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {(f.invoice_vat_exempt || 'false') === 'true' && (
              <div className="mt-3 space-y-2">
                <SettingField label="Riferimento Normativo IVA" value={f.invoice_vat_exempt_ref} onChange={v => set('finance', 'invoice_vat_exempt_ref', v)} placeholder="es. Esente IVA ai sensi dell'Art. 10 comma 1 n. 27-quinquies DPR 633/72" help="Testo che apparirà nelle fatture" />
                <SettingField label="Aliquota IVA (se non esente) %" value={f.invoice_vat_rate} onChange={v => set('finance', 'invoice_vat_rate', v)} type="number" placeholder="22" help="Usata solo se l'esenzione è disattivata" />
              </div>
            )}
          </div>
          <div className="bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-white font-medium text-sm">Esente Ritenuta d'Acconto</p>
                <p className="text-[10px] text-gray-500">Se attivo, non viene applicata la ritenuta d'acconto sui compensi</p>
              </div>
              <button onClick={() => set('finance', 'invoice_withholding_exempt', (f.invoice_withholding_exempt || 'false') === 'true' ? 'false' : 'true')} className={`w-12 h-6 rounded-full transition-colors ${(f.invoice_withholding_exempt || 'false') === 'true' ? 'bg-green-500' : 'bg-gray-600'}`}>
                <div className={`w-5 h-5 bg-white rounded-full transition-transform ${(f.invoice_withholding_exempt || 'false') === 'true' ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {(f.invoice_withholding_exempt || 'false') === 'true' && (
              <div className="mt-3 space-y-2">
                <SettingField label="Riferimento Normativo Ritenuta" value={f.invoice_withholding_exempt_ref} onChange={v => set('finance', 'invoice_withholding_exempt_ref', v)} placeholder="es. Esente ritenuta d'acconto ai sensi dell'Art. 25-bis DPR 600/73" help="Testo che apparirà nelle fatture" />
                <SettingField label="Aliquota Ritenuta (se non esente) %" value={f.invoice_withholding_rate} onChange={v => set('finance', 'invoice_withholding_rate', v)} type="number" placeholder="20" help="Usata solo se l'esenzione è disattivata" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== CODICI ===== */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center gap-2 mb-1"><Tag size={18} className="text-yellow-400" /><h2 className="font-bold text-white">Codici ISRC / UPC</h2></div>
        <p className="text-xs text-gray-500 mb-4">Prefissi per la generazione automatica dei codici</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SettingField label="Prefisso Paese ISRC" value={c.isrc_prefix} onChange={v => set('codes', 'isrc_prefix', v)} placeholder="IT" help="Codice paese del tuo prefisso FIMI" />
          <SettingField label="Registrant ISRC (3 caratteri)" value={c.isrc_registrant} onChange={v => set('codes', 'isrc_registrant', v)} placeholder="XXX" help="I 3 caratteri assegnati da FIMI" />
          <SettingField label="Prefisso UPC/EAN" value={c.upc_prefix} onChange={v => set('codes', 'upc_prefix', v)} help="Prefisso GS1 della tua azienda" />
        </div>
      </div>

      {/* ===== EMAIL ===== */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center gap-2 mb-1"><Mail size={18} className="text-yellow-400" /><h2 className="font-bold text-white">Configurazione Email</h2></div>
        <p className="text-xs text-gray-500 mb-4">Server SMTP Aruba. Puoi usare email diverse per supporto e comunicazioni.</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <SettingField label="Host SMTP" value={em.smtp_host} onChange={v => set('email', 'smtp_host', v)} placeholder="smtps.aruba.it" />
          <SettingField label="Porta" value={em.smtp_port} onChange={v => set('email', 'smtp_port', v)} type="number" placeholder="465" />
          <SettingField label="Utente SMTP" value={em.smtp_user} onChange={v => set('email', 'smtp_user', v)} placeholder="noreply@proudnoize.com" />
          <SettingField label="Password SMTP" value={em.smtp_pass} onChange={v => set('email', 'smtp_pass', v)} type="password" />
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={(em.smtp_secure || 'true') === 'true'} onChange={e => set('email', 'smtp_secure', e.target.checked ? 'true' : 'false')} />
              <span className="text-sm text-gray-300">Connessione sicura (SSL/TLS)</span>
            </label>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-4 mb-4">
          <h3 className="text-sm font-medium text-yellow-400 mb-3">📧 Email INFO (registrazioni, rendiconti, pagamenti, release)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SettingField label="Email Mittente (Info)" value={em.smtp_info_email} onChange={v => set('email', 'smtp_info_email', v)} placeholder="info@proudnoize.com" />
            <SettingField label="Nome Mittente (Info)" value={em.smtp_info_name} onChange={v => set('email', 'smtp_info_name', v)} placeholder="ProudNoize" />
          </div>
        </div>

        <div className="border-t border-gray-700 pt-4 mb-6">
          <h3 className="text-sm font-medium text-yellow-400 mb-3">🎧 Email SUPPORTO (ticket, take down, OAC)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SettingField label="Host SMTP Support" value={em.smtp_support_host} onChange={v => set('email', 'smtp_support_host', v)} placeholder="smtps.aruba.it" />
            <SettingField label="Porta SMTP Support" value={em.smtp_support_port} onChange={v => set('email', 'smtp_support_port', v)} type="number" placeholder="465" />
            <SettingField label="Utente SMTP Support" value={em.smtp_support_user} onChange={v => set('email', 'smtp_support_user', v)} placeholder="support@proudnoize.com" />
            <SettingField label="Password SMTP Support" value={em.smtp_support_pass} onChange={v => set('email', 'smtp_support_pass', v)} type="password" />
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4" checked={(em.smtp_support_secure || 'true') === 'true'} onChange={e => set('email', 'smtp_support_secure', e.target.checked ? 'true' : 'false')} />
                <span className="text-sm text-gray-300">Connessione sicura (SSL/TLS)</span>
              </label>
            </div>
            <SettingField label="Email Mittente (Support)" value={em.smtp_support_email} onChange={v => set('email', 'smtp_support_email', v)} placeholder="support@proudnoize.com" />
            <SettingField label="Nome Mittente (Support)" value={em.smtp_support_name} onChange={v => set('email', 'smtp_support_name', v)} placeholder="ProudNoize Support" />
          </div>
        </div>

        {/* TEST EMAIL */}
        <div className="border-t border-gray-700 pt-4">
          <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Send size={14} />Invia Email di Test</h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <input className="input-field-dark flex-1" placeholder="La tua email per il test..." value={testEmail} onChange={e => setTestEmail(e.target.value)} />
            <select className="input-field-dark w-40" value={testType} onChange={e => setTestType(e.target.value)}>
              <option value="info">📧 Email Info</option>
              <option value="support">🎧 Email Support</option>
            </select>
            <button onClick={sendTest} disabled={testing} className="btn-admin inline-flex items-center gap-2 whitespace-nowrap">
              {testing ? <><Loader size={14} className="animate-spin" />Invio...</> : <><Send size={14} />Invia Test</>}
            </button>
          </div>
          {testResult && (
            <div className={`mt-4 p-4 rounded-lg ${testResult.success ? 'bg-green-900/30 border border-green-800' : 'bg-red-900/30 border border-red-800'}`}>
              <div className="flex items-start gap-2">
                {testResult.success ? <CheckCircle size={18} className="text-green-400 mt-0.5" /> : <XCircle size={18} className="text-red-400 mt-0.5" />}
                <div>
                  <p className={`text-sm font-medium ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>{testResult.message}</p>
                  {testResult.from && <p className="text-xs text-gray-400 mt-1">Da: {testResult.from}</p>}
                  {testResult.details && <p className="text-xs text-red-300 mt-1">{testResult.details}</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== CREA UTENTE ===== */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center gap-2 mb-1"><Users size={18} className="text-yellow-400" /><h2 className="font-bold text-white">Crea Utente</h2></div>
        <p className="text-xs text-gray-500 mb-4">Crea un utente direttamente attivato senza passare dalla registrazione</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="block text-xs text-gray-400 mb-1">Nome *</label><input className="input-field-dark" value={newUser.first_name} onChange={e => setNewUser(p => ({ ...p, first_name: e.target.value }))} placeholder="Mario" /></div>
          <div><label className="block text-xs text-gray-400 mb-1">Cognome *</label><input className="input-field-dark" value={newUser.last_name} onChange={e => setNewUser(p => ({ ...p, last_name: e.target.value }))} placeholder="Rossi" /></div>
          <div><label className="block text-xs text-gray-400 mb-1">Email *</label><input type="email" className="input-field-dark" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="mario@email.com" /></div>
          <div><label className="block text-xs text-gray-400 mb-1">Password *</label><input type="text" className="input-field-dark" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} placeholder="Min. 8 caratteri" /></div>
          <div><label className="block text-xs text-gray-400 mb-1">Commissione PN %</label><input type="number" className="input-field-dark" value={newUser.pn_commission_percent} onChange={e => setNewUser(p => ({ ...p, pn_commission_percent: parseFloat(e.target.value) }))} min="0" max="100" /></div>
          <div className="flex items-end"><button onClick={createUser} disabled={creating} className="btn-admin w-full py-3">{creating ? 'Creazione...' : 'Crea Utente (Attivato)'}</button></div>
        </div>
      </div>

      {/* ===== PULIZIA STORAGE ===== */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center gap-2 mb-1"><HardDrive size={18} className="text-yellow-400" /><h2 className="font-bold text-white">Pulizia Storage</h2></div>
        <p className="text-xs text-gray-500 mb-4">Comprimi le copertine e rimuovi i file audio delle release già approvate/distribuite per liberare spazio</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => runCleanup(false)} disabled={cleaning} className="btn-admin inline-flex items-center gap-2">
            {cleaning ? <Loader size={16} className="animate-spin" /> : <Image size={16} />}
            Solo Copertine (250×250)
          </button>
          <button onClick={() => runCleanup(true)} disabled={cleaning} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-2 transition">
            {cleaning ? <Loader size={16} className="animate-spin" /> : <HardDrive size={16} />}
            Copertine + Elimina Audio
          </button>
        </div>
        {cleanupResult && (
          <div className="mt-4 p-4 bg-gray-800/50 rounded-lg text-sm">
            <p className="text-green-400 font-medium mb-2">Pulizia completata!</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div><p className="text-2xl font-bold text-white">{cleanupResult.releases}</p><p className="text-[10px] text-gray-400">Release trovate</p></div>
              <div><p className="text-2xl font-bold text-white">{cleanupResult.coversResized}</p><p className="text-[10px] text-gray-400">Copertine ridimensionate</p></div>
              <div><p className="text-2xl font-bold text-white">{cleanupResult.audioDeleted}</p><p className="text-[10px] text-gray-400">Audio eliminati</p></div>
              <div><p className="text-2xl font-bold text-yellow-400">{cleanupResult.spaceFreedMB} MB</p><p className="text-[10px] text-gray-400">Spazio liberato</p></div>
            </div>
            {cleanupResult.errors && <p className="text-xs text-red-400 mt-2">Errori: {cleanupResult.errors.join(', ')}</p>}
          </div>
        )}
      </div>

      <div className="flex justify-end mb-8">
        <button onClick={save} disabled={saving} className="btn-admin px-8 py-3 text-base inline-flex items-center gap-2"><Save size={18} />{saving ? 'Salvataggio...' : 'Salva Tutte le Impostazioni'}</button>
      </div>
    </div>
  );
}
